/**
 * Peptide-specific metadata parser for Saleor product metadata.
 * Used by Peptide Directory, PDP peptide blocks, Quiz, and Compare.
 */

type MetadataEntry = {
	key?: string | null;
	value?: string | null;
};

export type PeptideScores = {
	evidence: number;
	safety: number;
	efficacy: number;
	costBenefit: number;
	easeOfUse: number;
	overall: number;
};

export type PeptideMetadata = {
	researchLevel: string | null;
	availability: string | null;
	mechanism: string | null;
	typicalDosage: string | null;
	halfLife: string | null;
	sequence: string | null;
	pubmedUrl: string | null;
	aliases: string[];
	peptideCategories: string[];
	scores: PeptideScores;
	// Quiz optimization fields
	timing: string | null;
	dosingFrequency: string | null;
	contraindicated: string[];
};

const DEFAULT_SCORES: PeptideScores = {
	evidence: 5,
	safety: 5,
	efficacy: 5,
	costBenefit: 5,
	easeOfUse: 5,
	overall: 5,
};

function metadataToMap(metadata: MetadataEntry[] | null | undefined): Record<string, string> {
	if (!metadata || metadata.length === 0) return {};
	return metadata.reduce<Record<string, string>>((acc, item) => {
		if (item?.key && item.value) acc[item.key] = item.value;
		return acc;
	}, {});
}

function parseJsonArray(value: string | null | undefined): string[] {
	if (!value) return [];
	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
	} catch {
		return [];
	}
}

function parseScores(value: string | null | undefined): PeptideScores {
	if (!value) return DEFAULT_SCORES;
	try {
		const parsed = JSON.parse(value) as Record<string, unknown>;
		if (!parsed || typeof parsed !== "object") return DEFAULT_SCORES;
		const num = (v: unknown): number => (typeof v === "number" && !Number.isNaN(v) ? v : 5);
		return {
			evidence: num(parsed.evidence),
			safety: num(parsed.safety),
			efficacy: num(parsed.efficacy),
			costBenefit: num(parsed.costBenefit),
			easeOfUse: num(parsed.easeOfUse),
			overall: num(parsed.overall),
		};
	} catch {
		return DEFAULT_SCORES;
	}
}

/**
 * Parse peptide-specific metadata from Saleor product metadata.
 * Returns null if no peptide metadata is present (e.g. non-peptide product).
 */
export function parsePeptideMetadata(metadata: MetadataEntry[] | null | undefined): PeptideMetadata | null {
	const meta = metadataToMap(metadata);

	// Consider it a peptide if we have research_level or peptide_scores
	const hasPeptideData =
		meta.ux_research_level || meta.ux_peptide_scores_json || meta.ux_peptide_categories_json;

	if (!hasPeptideData) {
		return null;
	}

	const scores = parseScores(meta.ux_peptide_scores_json);

	return {
		researchLevel: meta.ux_research_level ?? null,
		availability: meta.ux_availability ?? null,
		mechanism: meta.ux_mechanism ?? null,
		typicalDosage: meta.ux_typical_dosage ?? null,
		halfLife: meta.ux_half_life ?? null,
		sequence: meta.ux_sequence ?? null,
		pubmedUrl: meta.ux_pubmed_url ?? null,
		aliases: parseJsonArray(meta.ux_aliases_json),
		peptideCategories: parseJsonArray(meta.ux_peptide_categories_json),
		scores,
		// Quiz optimization fields
		timing: meta.ux_timing ?? null,
		dosingFrequency: meta.ux_dosing_frequency ?? null,
		contraindicated: parseJsonArray(meta.ux_contraindicated_combinations_json),
	};
}

/**
 * Check if a product has peptide metadata (for PDP conditional rendering).
 */
export function hasPeptideMetadata(metadata: MetadataEntry[] | null | undefined): boolean {
	return parsePeptideMetadata(metadata) !== null;
}
